import { useRef } from "react";
import { useAuth } from "@/context/AuthContext";
import { useSettings } from "@/context/SettingsContext";
import { fileToResizedDataUrl } from "@/lib/image";
import { toast } from "sonner";
import { Camera } from "lucide-react";

function initialsOf(name) {
  if (!name) return "HW";
  const parts = name.replace(/[^\p{L}\p{N}\s-]/gu, " ").split(/\s|-/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

export default function BrandLogo({ size = 36, rounded = "rounded-xl", editable = true, className = "" }) {
  const { user } = useAuth();
  const { settings, update } = useSettings();
  const inputRef = useRef(null);
  const isAdmin = user && user.role === "admin";
  const canEdit = editable && isAdmin;

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 4 * 1024 * 1024) return toast.error("Immagine troppo grande (max 4MB)");
    try {
      const dataUrl = await fileToResizedDataUrl(file, 512, 0.9);
      await update({ app_logo: dataUrl });
      toast.success("Logo aggiornato");
    } catch {
      toast.error("Errore caricamento logo");
    }
  };

  const label = initialsOf(settings.app_name);

  const inner = settings.app_logo ? (
    <img
      src={settings.app_logo}
      alt={settings.app_name || "logo"}
      className="h-full w-full object-cover"
      draggable={false}
    />
  ) : (
    <img
      src={`${process.env.PUBLIC_URL || ""}/hi-work-logo.png`}
      alt={settings.app_name || "Hi-Work"}
      className="h-full w-full object-cover"
      draggable={false}
    />
  );

  if (!canEdit) {
    return (
      <div
        data-testid="brand-logo"
        className={`${rounded} overflow-hidden shadow-lg shadow-fuchsia-600/25 shrink-0 ${className}`}
        style={{ width: size, height: size }}
      >
        {inner}
      </div>
    );
  }

  return (
    <>
      <button
        onClick={() => inputRef.current?.click()}
        data-testid="brand-logo-editable"
        title="Cambia logo (admin)"
        className={`${rounded} overflow-hidden shadow-lg shadow-fuchsia-600/25 shrink-0 relative group focus:outline-none focus:ring-2 focus:ring-fuchsia-500/60 ${className}`}
        style={{ width: size, height: size }}
      >
        {inner}
        <span className="absolute inset-0 flex items-center justify-center bg-black/0 group-hover:bg-black/50 opacity-0 group-hover:opacity-100 transition-colors">
          <Camera size={size * 0.35} className="text-white" />
        </span>
      </button>
      <input
        ref={inputRef}
        data-testid="brand-logo-input"
        type="file"
        accept="image/*"
        className="hidden"
        onChange={onFile}
      />
    </>
  );
}
